import { SYSTEM_LOINC, SYSTEM_SNOMED } from '@ctw/shared/api/fhir/system-urls';

export interface Code {
  system: string;
  code: string;
}

export interface ValueSet {
  display: string;
  content: Array<Code>;
}

export const VitalsValueSets: Array<ValueSetKey> = [
  'bpSystolic',
  'bpDiastolic',
  'bpCombined',
  'heartRate',
  'temperature',
  'respiratoryRate',
  'o2',
  'height',
  'weight',
  'bmi',
  'headCircumference',
  'lmp',
  'pain',
];

export const TrendAnalytes: Array<ValueSetKey> = [
  'a1c',
  'ldl',
  'cholesterol',
  'hdl',
  'triglycerides',
  'gfr',
];

export const getValueSet = (valueSetId: string) => {
  return Object.prototype.hasOwnProperty.call(ValueSets, valueSetId)
    ? ValueSets[valueSetId as ValueSetKey]
    : undefined;
};

export const ValueSets = {
  a1c: {
    display: 'A1c',
    content: [
      { system: SYSTEM_LOINC, code: '83036' },
      { system: SYSTEM_LOINC, code: '83037' },
      { system: SYSTEM_LOINC, code: '17855-8' },
      { system: SYSTEM_LOINC, code: '17856-6' },
      { system: SYSTEM_LOINC, code: '4548-4' },
      { system: SYSTEM_LOINC, code: '4549-2' },
      { system: SYSTEM_LOINC, code: '96595-4' },
      { system: SYSTEM_SNOMED, code: '43396009' },
      { system: SYSTEM_SNOMED, code: '313835008' },
    ],
  },
  ldl: {
    display: 'LDL',
    content: [
      { system: SYSTEM_LOINC, code: '18262-6' },
      { system: SYSTEM_LOINC, code: '13457-7' },
      { system: SYSTEM_LOINC, code: '2089-1' },
      { system: SYSTEM_LOINC, code: '39469-2' },
      { system: SYSTEM_LOINC, code: '46985-8' },
    ],
  },
  cholesterol: {
    display: 'Cholesterol',
    content: [
      { system: SYSTEM_LOINC, code: '2093-3' },
      { system: SYSTEM_LOINC, code: '48620-9' },
      { system: SYSTEM_LOINC, code: '35200-5' },
    ],
  },
  hdl: {
    display: 'HDL',
    content: [
      { system: SYSTEM_LOINC, code: '2085-9' },
      { system: SYSTEM_LOINC, code: '2086-7' },
      { system: SYSTEM_LOINC, code: '49130-8' },
      { system: SYSTEM_LOINC, code: '9832-7' },
    ],
  },
  glucose: {
    display: 'Glucose',
    content: [{ system: SYSTEM_LOINC, code: '2345-7' }],
  },
  lipase: {
    display: 'Lipase',
    content: [{ system: SYSTEM_LOINC, code: '3040-3' }],
  },
  phosphate: {
    display: 'Phosphate',
    content: [{ system: SYSTEM_LOINC, code: '2777-1' }],
  },
  potassium: {
    display: 'Potassium',
    content: [{ system: SYSTEM_LOINC, code: '2823-3' }],
  },
  thyrotropin: {
    display: 'Thyrotropin',
    content: [{ system: SYSTEM_LOINC, code: '3016-3' }],
  },
  thyroxineT4Free: {
    display: 'Thyroxine (T4) free',
    content: [{ system: SYSTEM_LOINC, code: '3024-7' }],
  },
  ureaNitrogen: {
    display: 'Urea Nitrogen',
    content: [{ system: SYSTEM_LOINC, code: '3094-0' }],
  },
  triglycerides: {
    display: 'Triglycerides',
    content: [
      { system: SYSTEM_LOINC, code: '2571-8' },
      { system: SYSTEM_LOINC, code: '12951-0' },
      { system: SYSTEM_LOINC, code: '3043-7' },
      { system: SYSTEM_LOINC, code: '14927-8' },
      { system: SYSTEM_LOINC, code: '3048-6' },
      { system: SYSTEM_LOINC, code: '3049-4' },
    ],
  },
  gfr: {
    display: 'GFR',
    content: [
      { system: SYSTEM_LOINC, code: '48643-1' },
      { system: SYSTEM_LOINC, code: '33914-3' },
      { system: SYSTEM_LOINC, code: '77147-7' },
      { system: SYSTEM_LOINC, code: '50044-7' },
      { system: SYSTEM_LOINC, code: '70969-1' },
      { system: SYSTEM_LOINC, code: '48642-3' },
      { system: SYSTEM_LOINC, code: '88294-4' },
      { system: SYSTEM_LOINC, code: '88293-6' },
      { system: SYSTEM_LOINC, code: '62238-1' },
      { system: SYSTEM_LOINC, code: '69405-9' },
    ],
  },
  bpSystolic: {
    display: 'BP Systolic',
    content: [
      { system: SYSTEM_LOINC, code: '8480-6' },
      { system: SYSTEM_LOINC, code: '8479-8' },
      { system: SYSTEM_LOINC, code: '8459-0' },
      { system: SYSTEM_LOINC, code: '8460-8' },
    ],
  },
  bpDiastolic: {
    display: 'BP Diastolic',
    content: [
      { system: SYSTEM_LOINC, code: '8462-4' },
      { system: SYSTEM_LOINC, code: '8453-3' },
      { system: SYSTEM_LOINC, code: '8454-1' },
    ],
  },
  bpCombined: {
    display: 'BP Combined',
    content: [{ system: SYSTEM_LOINC, code: '85354-9' }],
  },
  heartRate: {
    display: 'Heart Rate',
    content: [
      { system: SYSTEM_LOINC, code: '8867-4' },
      { system: SYSTEM_LOINC, code: '8893-0' },
    ],
  },
  temperature: {
    display: 'Temperature',
    content: [
      { system: SYSTEM_LOINC, code: '8310-5' },
      { system: SYSTEM_LOINC, code: '8331-1' },
    ],
  },
  respiratoryRate: {
    display: 'Respiratory Rate',
    content: [{ system: SYSTEM_LOINC, code: '9279-1' }],
  },
  o2: {
    display: 'Oxygen Saturation',
    content: [
      { system: SYSTEM_LOINC, code: '59408-5' },
      { system: SYSTEM_LOINC, code: '2710-2' },
      { system: SYSTEM_LOINC, code: '2711-0' },
      { system: SYSTEM_LOINC, code: '20564-1' },
      { system: SYSTEM_LOINC, code: '59418-4' },
      { system: SYSTEM_LOINC, code: '2709-4' },
      { system: SYSTEM_LOINC, code: '59416-8' },
      { system: SYSTEM_LOINC, code: '19224-5' },
    ],
  },
  height: {
    display: 'Height',
    content: [
      { system: SYSTEM_LOINC, code: '8302-2' },
      { system: SYSTEM_LOINC, code: '8306-3' },
      { system: SYSTEM_LOINC, code: '8301-4' },
      { system: SYSTEM_LOINC, code: '3138-5' },
    ],
  },
  weight: {
    display: 'Weight',
    content: [
      { system: SYSTEM_LOINC, code: '29463-7' },
      { system: SYSTEM_LOINC, code: '3141-9' },
      { system: SYSTEM_LOINC, code: '79348-9' },
    ],
  },
  bmi: {
    display: 'BMI',
    content: [
      { system: SYSTEM_LOINC, code: '39156-5' },
      { system: SYSTEM_LOINC, code: '41909-3' },
    ],
  },
  headCircumference: {
    display: 'Head Circumference',
    content: [
      { system: SYSTEM_LOINC, code: '9843-4' },
      { system: SYSTEM_LOINC, code: '8287-5' },
      { system: SYSTEM_LOINC, code: '1554118' },
    ],
  },
  lmp: {
    display: 'LMP',
    content: [
      { system: SYSTEM_LOINC, code: '8665-2' },
      { system: SYSTEM_LOINC, code: '3145-0' },
    ],
  },
  pain: {
    display: 'Pain',
    content: [
      { system: SYSTEM_LOINC, code: '95627-6' },
      { system: SYSTEM_LOINC, code: '52500-6' },
      { system: SYSTEM_LOINC, code: '72514-3' },
    ],
  },
} satisfies Record<string, ValueSet>;

export type ValueSetKey = keyof typeof ValueSets;
